import { useState } from "react";
import { generateProposal, saveItem } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, Copy, Save, ArrowLeft, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const fieldBase = "mt-2 bg-white rounded-2xl h-12 border border-black/10 focus-visible:ring-brand-purple";
const taBase = "mt-2 bg-white rounded-2xl border border-black/10 resize-none focus-visible:ring-brand-purple";
const lbl = "text-sm font-semibold text-foreground/85";

const COUNTRIES = [
  { v: "US", l: "🇺🇸 USA / Internacional", currency: "USD", pricePh: "$1,200 USD" },
  { v: "MX", l: "🇲🇽 México", currency: "MXN", pricePh: "$18,000 MXN" },
  { v: "AR", l: "🇦🇷 Argentina", currency: "ARS", pricePh: "$450.000 ARS" },
  { v: "BR", l: "🇧🇷 Brasil", currency: "BRL", pricePh: "R$ 4.900" },
  { v: "ES", l: "🇪🇸 España", currency: "EUR", pricePh: "1.200 €" },
];

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderMarkdown(md) {
  if (!md) return "";
  // Escapamos HTML primero para neutralizar cualquier script/iframe que Claude pueda devolver
  let html = escapeHtml(md)
    .replace(/^### (.*$)/gim, '<h3 class="font-display text-xl font-black tracking-tight mt-8 mb-3">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="font-display text-2xl font-black tracking-tight mt-10 mb-4">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="font-display text-4xl font-black tracking-tight mt-4 mb-6">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-foreground">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic text-foreground/70">$1</em>')
    .replace(/^- (.*$)/gim, '<li class="ml-4 text-foreground/75">— $1</li>')
    .replace(/\n\n/g, '</p><p class="my-4 text-foreground/75 leading-relaxed">');
  return '<p class="my-4 text-foreground/75 leading-relaxed">' + html + '</p>';
}

export default function ProposalGenerator() {
  const [form, setForm] = useState({
    client_name: "",
    offer_summary: "",
    tier_name: "Estratégico",
    price: "",
    country: "US",
    timeline: "",
    positioning_angle: "",
    deliverables: "",
    strategic_outcomes: "",
  });
  const [loading, setLoading] = useState(false);
  const [markdown, setMarkdown] = useState("");
  const [errorField, setErrorField] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const errCls = (k) => (errorField === k ? "!border-rose-500 ring-1 ring-rose-500/40" : "");

  const onGenerate = async () => {
    if (!form.client_name?.trim()) {
      setErrorField("client_name");
      toast.error("Indica el nombre del cliente");
      setTimeout(() => { const el = document.getElementById("field-client-name"); if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); } }, 100);
      return;
    }
    if (!form.offer_summary?.trim()) {
      setErrorField("offer_summary");
      toast.error("Añade un resumen de la oferta");
      setTimeout(() => { const el = document.getElementById("field-offer-summary"); if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); } }, 100);
      return;
    }
    setErrorField(null);
    setLoading(true);
    try {
      const r = await generateProposal({
        ...form,
        deliverables: form.deliverables.split("\n").map((x) => x.trim()).filter(Boolean),
        strategic_outcomes: form.strategic_outcomes.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      setMarkdown(r.markdown);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Propuesta lista");
    } catch (e) {
      console.error(e);
      toast.error(e.userMessage || "Error generando la propuesta");
    } finally {
      setLoading(false);
    }
  };

  const onCopy = async () => {
    await navigator.clipboard.writeText(markdown);
    toast.success("Copiado al portapapeles");
  };

  const onSave = async () => {
    if (!markdown) return;
    try {
      await saveItem({ kind: "proposal", title: form.client_name, payload: { input: form, markdown } });
      toast.success("Propuesta guardada");
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center relative">
        <div className="blob blob-lavender w-[300px] h-[300px] top-0 left-1/3" />
        <div className="blob blob-rose w-[300px] h-[300px] bottom-0 right-1/3" style={{ animationDelay: "2s" }} />
        <div className="text-center max-w-sm relative">
          <div className="w-14 h-14 mx-auto rounded-full bg-foreground grid place-items-center mb-6">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
          </div>
          <h2 className="font-display text-2xl font-black tracking-[-0.02em]">Escribiendo tu propuesta...</h2>
          <p className="text-sm text-foreground/65 mt-3">Tono seguro, sereno, sin pedir perdón por el precio.</p>
        </div>
      </div>
    );
  }

  if (markdown) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto animate-fade-up">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => { setMarkdown(""); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-apple-outline text-sm" data-testid="back-to-form-proposal">
            <ArrowLeft className="w-3.5 h-3.5" /> Editar datos
          </button>
          <button onClick={onCopy} className="btn-apple-outline text-sm" data-testid="copy-proposal-btn">
            <Copy className="w-3.5 h-3.5" /> Copiar texto
          </button>
          <button onClick={onSave} className="btn-apple text-sm" data-testid="save-proposal-btn">
            <Save className="w-3.5 h-3.5" /> Guardar
          </button>
        </div>
        <div className="app-card p-10">
          <article className="max-w-none" data-testid="proposal-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <header className="mb-10 relative">
        <div className="blob blob-rose w-[220px] h-[220px] -top-8 left-10" />
        <div className="blob blob-lavender w-[200px] h-[200px] top-0 right-1/4" style={{ animationDelay: "2s" }} />
        <div className="relative">
          <span className="tag-pill tag-pill-rose mb-5"><FileText className="w-3 h-3" /> Generar propuesta</span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.04em] leading-[0.95]">
            Una propuesta premium<br />
            en <span className="scribble-underline">segundos</span>.
          </h1>
          <p className="text-base text-foreground/65 mt-5 max-w-xl leading-relaxed">
            Escribo un documento estilo Linear/Notion que reposiciona tu trabajo como impacto de negocio.
          </p>
        </div>
      </header>

      <div className="app-card p-7 md:p-9 space-y-5">
        <div>
          <Label className={lbl}>Cliente <span className="text-brand-purple">*</span></Label>
          <Input id="field-client-name" data-testid="input-client-name" value={form.client_name} onChange={upd("client_name")} placeholder="Cliente Inc." className={`${fieldBase} ${errCls("client_name")}`} />
          {errorField === "client_name" && <p className="text-xs text-rose-600 mt-1.5 font-medium">Indica el nombre del cliente.</p>}
        </div>
        <div>
          <Label className={lbl}>Resumen de la oferta <span className="text-brand-purple">*</span></Label>
          <Textarea id="field-offer-summary" data-testid="input-summary" rows={3} value={form.offer_summary} onChange={upd("offer_summary")} placeholder="Qué tipo de proyecto, qué alcance, qué problema resuelves..." className={`${taBase} ${errCls("offer_summary")}`} />
          {errorField === "offer_summary" && <p className="text-xs text-rose-600 mt-1.5 font-medium">Añade un resumen breve de la oferta.</p>}
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          <div>
            <Label className={lbl}>Pack</Label>
            <Input data-testid="input-tier" value={form.tier_name} onChange={upd("tier_name")} className={fieldBase} />
          </div>
          <div>
            <Label className={lbl}>País del cliente <span className="text-brand-purple">*</span></Label>
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
              <SelectTrigger data-testid="select-country" className={fieldBase}><SelectValue /></SelectTrigger>
              <SelectContent>{COUNTRIES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l} · {o.currency}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className={lbl}>Inversión ({COUNTRIES.find((c) => c.v === form.country)?.currency || "USD"})</Label>
            <Input data-testid="input-price" value={form.price} onChange={upd("price")} placeholder={COUNTRIES.find((c) => c.v === form.country)?.pricePh || "1200"} className={fieldBase} />
          </div>
          <div>
            <Label className={lbl}>Plazo</Label>
            <Input data-testid="input-timeline" value={form.timeline} onChange={upd("timeline")} placeholder="6 semanas..." className={fieldBase} />
          </div>
        </div>
        <div>
          <Label className={lbl}>Ángulo de posicionamiento</Label>
          <Input data-testid="input-angle" value={form.positioning_angle} onChange={upd("positioning_angle")} placeholder="Transformación estratégica, claridad de marca..." className={fieldBase} />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className={lbl}>Entregables</Label>
            <Textarea rows={4} value={form.deliverables} onChange={upd("deliverables")} placeholder="Uno por línea" className={taBase} />
          </div>
          <div>
            <Label className={lbl}>Resultados estratégicos</Label>
            <Textarea rows={4} value={form.strategic_outcomes} onChange={upd("strategic_outcomes")} placeholder="Uno por línea" className={taBase} />
          </div>
        </div>
        <button onClick={onGenerate} disabled={loading} data-testid="generate-proposal-btn" className="btn-apple w-full justify-center text-base h-14">
          <Sparkles className="w-4 h-4" /> Generar propuesta <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
